import {Article, ArticlePart, ArticleQuestion} from "@/model/Article";
import {stripTagsAndEntities} from "@/utils/string";
import {textCoversKeyword} from "@/service/keywordStemDensity";
import {getContentToExport} from "@/service/articleExport";
import {DownloadType} from "@/enum/download_type";
import {hasValidSchemaMarkup} from "@/service/seoScoreSchema";
import {eatBioIsFilled, eatDateIsFilled, hasEatAuthor} from "@/service/extractEatAttribution";

export type AiPlatformId = 'googleAio' | 'chatgpt' | 'perplexity' | 'copilot';
export type EeatPillarId = 'experience' | 'expertise' | 'authoritativeness' | 'trustworthiness';

export type ScoredFactor = {
    id: string;
    ok: boolean;
};

export type AiVisibilityReport = {
    score: number;
    platforms: Array<{ id: AiPlatformId; score: number }>;
    factors: ScoredFactor[];
};

export type EeatReport = {
    score: number;
    pillars: Array<{ id: EeatPillarId; score: number }>;
    signals: Array<{ id: string; pillar: EeatPillarId; ok: boolean }>;
    actions: string[];
};

const EXPERIENCE_RE = /vyzkouš|testovali|otestov|zkušenost|v praxi|osobně|recenze|případov|case study|we tested|i tried|hands-on|first-?hand|our experience/i;
const REVIEW_RE = /recenze|hodnocení zákaz|case study|případová studie|testovali jsme|customer review/i;
const AUTHOR_RE = /autor:|napsal|redakce|editor|byline|reviewed by|odborný garant/i;
const TRUST_RE = /kontakt|i[cč]o|gdpr|podmínky|zásady|privacy|disclaimer|zdroj:|https:\/\//i;
const PRICE_RE = /cena|ceník|kč|eur|\bprice\b|pricing|zdarma|poplat/i;
const FACT_RE = /\d+([.,]\d+)?\s?(%|kg|kč|€|\$|let|gb|ms|mb|km)|(?:rok(?:u)?\s)?(?:19|20)\d{2}|\b\d{2,}\b/i;
const QUESTION_MARK_RE = /[?¿]/;
const QUESTION_START_RE = /^(co |jak |pro[cč] |kdy |kde |kdo |kter |k [cč]emu |na co |who |what |how |why |when |which |does |do |did |is |are |can |should |will |would |could )/i;
const COMPARISON_RE = /srovn|porovn|versus|\bvs\.?\b|výhod|nevýhod|compar/i;

function clamp(value: number): number {
    return Math.max(0, Math.min(100, Math.round(value)));
}

function mergedText(article: Article): string {
    let merged = '';
    article.getAllParts().forEach((part: ArticlePart) => {
        part.value.forEach(block => {
            merged += ` ${block.h} ${block.p} `;
        });
    });
    (article.questions || []).forEach((item: ArticleQuestion) => {
        merged += ` ${item.question} ${item.answer} `;
    });
    merged += ` ${article.metaTitle} ${article.metaDescription}`;
    return stripTagsAndEntities(merged);
}

function completeFaqs(article: Article): ArticleQuestion[] {
    return (article.questions || []).filter(item =>
        stripTagsAndEntities(item.question) && stripTagsAndEntities(item.answer)
    );
}

function looksLikeQuestion(text: string): boolean {
    const value = stripTagsAndEntities(text).replace(/\s+/g, ' ').trim();
    if (!value) {
        return false;
    }
    if (QUESTION_MARK_RE.test(value)) {
        return true;
    }
    return QUESTION_START_RE.test(value + ' ');
}

type QaPair = {question: string; answer: string};

export function qaPairsFromArticle(article: Article): QaPair[] {
    const pairs: QaPair[] = completeFaqs(article).map((item) => ({
        question: stripTagsAndEntities(item.question),
        answer: stripTagsAndEntities(item.answer),
    }));
    const seen = new Set(pairs.map((item) => item.question.toLowerCase()));
    article.getAllParts().forEach((part: ArticlePart) => {
        part.value.forEach((block) => {
            const question = stripTagsAndEntities(block.h);
            const answer = stripTagsAndEntities(block.p);
            if (!looksLikeQuestion(question) || answer.length < 20) {
                return;
            }
            const key = question.toLowerCase();
            if (seen.has(key)) {
                return;
            }
            seen.add(key);
            pairs.push({question, answer});
        });
    });
    return pairs;
}

function containsKeyword(haystack: string, keyword: string): boolean {
    return textCoversKeyword(haystack, keyword);
}

function headingCount(article: Article, level: number): number {
    const key = ('h' + level) as 'h1' | 'h2' | 'h3';
    return (article[key] || []).filter(block => stripTagsAndEntities(block.h)).length;
}

function countLinks(html: string, kind: 'in' | 'out', host: string | null): number {
    const matches = html.match(/<a[^>]+href=["']([^"']+)["'][^>]*>/gi) || [];
    return matches.filter(tag => {
        const href = (tag.match(/href=["']([^"']+)/i) || [])[1] || '';
        if (!href || href.startsWith('#') || href.startsWith('mailto:')) return false;
        const isRelative = href.startsWith('/');
        const sameHost = host ? href.includes(host) : false;
        return kind === 'in' ? (isRelative || sameHost) : (!isRelative && !sameHost && /^https?:/i.test(href));
    }).length;
}

function pageHost(article: Article): string | null {
    try {
        return article.url ? new URL(article.url).host : null;
    } catch {
        return null;
    }
}

function weighted(score: number, weight: number): number {
    return score * weight;
}

export function evaluateAiVisibility(article: Article): AiVisibilityReport {
    const text = mergedText(article);
    const html = getContentToExport(article, DownloadType.HTML)[0] || '';
    const host = pageHost(article);
    const faqs = completeFaqs(article);
    const qaPairs = qaPairsFromArticle(article);
    const schema = article.schemaJsonLd || '';
    const keyword = (article.selectedKeyword || article.keyword || '').toLowerCase();
    const intro = stripTagsAndEntities(article.h1[0]?.p || '');
    const h1 = stripTagsAndEntities(article.h1[0]?.h || '');
    const year = new Date().getFullYear();
    const outbound = countLinks(html, 'out', host);

    const conversationalFaq = qaPairs.length > 0;
    const hasSchema = hasValidSchemaMarkup(schema);
    const hasFaqSchema = /FAQPage/i.test(schema);
    const hasFacts = FACT_RE.test(text) || COMPARISON_RE.test(text);
    const hasOutbound = outbound >= 1;
    const fresh = new RegExp(String(year - 1) + '|' + String(year)).test(text)
        || (!article.isAnalyze && (!article.created_at || Date.now() - article.created_at < 1000 * 60 * 60 * 24 * 400))
        || (article.created_at ? Date.now() - article.created_at < 1000 * 60 * 60 * 24 * 400 : false);
    const leadAnswersIntent = !keyword || containsKeyword(`${h1} ${intro}`, keyword);
    const qaAnswersIntent = !!keyword && qaPairs.some((item) =>
        containsKeyword(`${item.question} ${item.answer}`, keyword)
    );
    const directAnswer = leadAnswersIntent || qaAnswersIntent;
    const structure = headingCount(article, 2) >= 2;

    const factors: ScoredFactor[] = [
        {id: 'faq', ok: conversationalFaq},
        {id: 'schema', ok: hasSchema && (hasFaqSchema || faqs.length === 0 || conversationalFaq)},
        {id: 'facts', ok: hasFacts},
        {id: 'outbound', ok: hasOutbound},
        {id: 'freshness', ok: fresh},
        {id: 'answers', ok: !!directAnswer},
    ];

    const base = (
        weighted(conversationalFaq ? 100 : 0, 0.22)
        + weighted(hasSchema ? 100 : 0, 0.16)
        + weighted(hasFaqSchema ? 100 : (hasSchema ? 40 : 0), 0.06)
        + weighted(hasFacts ? 100 : 0, 0.16)
        + weighted(hasOutbound ? 100 : 0, 0.12)
        + weighted(fresh ? 100 : 0, 0.10)
        + weighted(directAnswer ? 100 : 0, 0.12)
        + weighted(structure ? 100 : 0, 0.06)
    );

    const tweak = (opts: { faq?: number; schema?: number; facts?: number; outbound?: number; fresh?: number; answers?: number }) => {
        const value = (
            (conversationalFaq ? 100 : 0) * (opts.faq || 1) * 0.22
            + (hasSchema ? 100 : 0) * (opts.schema || 1) * 0.18
            + (hasFacts ? 100 : 0) * (opts.facts || 1) * 0.16
            + (hasOutbound ? 100 : 0) * (opts.outbound || 1) * 0.12
            + (fresh ? 100 : 0) * (opts.fresh || 1) * 0.10
            + (directAnswer ? 100 : 0) * (opts.answers || 1) * 0.12
            + (structure ? 100 : 0) * 0.10
        );
        return clamp(value);
    };

    const platforms: AiVisibilityReport['platforms'] = [
        {id: 'googleAio', score: tweak({schema: 1.25, faq: 1.2, answers: 1.15, outbound: 0.85})},
        {id: 'chatgpt', score: tweak({faq: 1.3, facts: 1.2, outbound: 1.1})},
        {id: 'perplexity', score: tweak({outbound: 1.4, facts: 1.2, fresh: 1.25, schema: 1.1})},
        {id: 'copilot', score: tweak({schema: 1.25, fresh: 1.2, outbound: 1.2, answers: 1.2, facts: 1.15, faq: 1.1})},
    ];

    const score = clamp(platforms.reduce((sum, item) => sum + item.score, base) / (platforms.length + 1));

    return {score, platforms, factors};
}

export function evaluateEeat(article: Article): EeatReport {
    const text = mergedText(article);
    const html = getContentToExport(article, DownloadType.HTML)[0] || '';
    const host = pageHost(article);
    const faqs = qaPairsFromArticle(article);
    const schemaOk = hasValidSchemaMarkup(article.schemaJsonLd);
    const keyword = (article.selectedKeyword || article.keyword || '').toLowerCase();
    const wordCount = stripTagsAndEntities(text).split(/\s+/).filter(Boolean).length;
    const outbound = countLinks(html, 'out', host);
    const inbound = countLinks(html, 'in', host);
    const httpsOk = article.isAnalyze
        ? /^https:\/\//i.test(article.url || '')
        : (!article.url || /^https:\/\//i.test(article.url));
    const year = new Date().getFullYear();
    const fresh = new RegExp(String(year)).test(text)
        || (!article.created_at || Date.now() - article.created_at < 1000 * 60 * 60 * 24 * 400);
    const h2s = headingCount(article, 2);
    const experienceLang = EXPERIENCE_RE.test(text);
    const reviews = REVIEW_RE.test(text);
    const namedAuthor = hasEatAuthor(article.eatAttribution);
    const author = namedAuthor || AUTHOR_RE.test(text);
    const authorBio = eatBioIsFilled(article.eatAttribution);
    const dated = eatDateIsFilled(article.eatAttribution);
    const facts = FACT_RE.test(text);
    const trustCue = TRUST_RE.test(text) || httpsOk;
    const priceCue = PRICE_RE.test(text);
    const keywordInH1 = keyword ? textCoversKeyword(article.h1[0]?.h || '', keyword, article.language_code) : false;

    const signals: EeatReport['signals'] = [
        {id: 'reviews', pillar: 'experience', ok: reviews || experienceLang},
        {id: 'author', pillar: 'experience', ok: author},
        {id: 'authorBio', pillar: 'expertise', ok: authorBio},
        {id: 'freshDate', pillar: 'trustworthiness', ok: dated || fresh},
        {id: 'specialization', pillar: 'expertise', ok: h2s >= 2 && wordCount >= 400},
        {id: 'depth', pillar: 'expertise', ok: wordCount >= 800 || h2s >= 3},
        {id: 'mentions', pillar: 'authoritativeness', ok: outbound >= 1 || inbound >= 1},
        {id: 'citations', pillar: 'authoritativeness', ok: outbound >= 1},
        {id: 'https', pillar: 'trustworthiness', ok: httpsOk},
        {id: 'transparency', pillar: 'trustworthiness', ok: trustCue || priceCue || schemaOk},
    ];

    const pillars: EeatReport['pillars'] = [
        {
            id: 'experience',
            score: clamp((experienceLang ? 30 : 0) + (reviews ? 20 : 0) + (facts ? 15 : 0) + (namedAuthor ? 25 : (author ? 10 : 0)) + (faqs.length ? 10 : 0)),
        },
        {
            id: 'expertise',
            score: clamp((wordCount >= 800 ? 25 : wordCount >= 400 ? 15 : 6) + (h2s >= 3 ? 20 : h2s >= 2 ? 12 : 0) + (faqs.length ? 15 : 0) + (keywordInH1 ? 12 : 0) + (facts ? 10 : 0) + (authorBio ? 18 : 0)),
        },
        {
            id: 'authoritativeness',
            score: clamp((outbound ? 25 : 0) + (inbound ? 15 : 0) + (schemaOk ? 20 : 0) + (keywordInH1 ? 12 : 0) + (faqs.length ? 10 : 0) + (namedAuthor ? 18 : 0)),
        },
        {
            id: 'trustworthiness',
            score: clamp((httpsOk ? 20 : 8) + (trustCue ? 15 : 0) + (schemaOk ? 15 : 0) + (faqs.length ? 10 : 0) + (dated ? 25 : (fresh ? 15 : 0)) + (namedAuthor ? 15 : 0)),
        },
    ];

    const score = clamp(pillars.reduce((sum, item) => sum + item.score, 0) / pillars.length);
    const actions = signals.filter(item => !item.ok).slice(0, 3).map(item => item.id);

    return {score, pillars, signals, actions};
}

export function scoreStatusThreshold(score: number): 'GREEN' | 'YELLOW' | 'RED' {
    if (score >= 70) return 'GREEN';
    if (score >= 45) return 'YELLOW';
    return 'RED';
}
